// Location: src/components/admin/content-machine/ContentMachineAdminController.tsx
// Purpose: Orchestrates Content Machine state, queries, mutations, and workflow handlers.
// Scope: Keeps canonical route behavior wired to the extracted ContentMachineAdminLayout.

/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { cn } from '@/lib/utils';
import { useMutation, useAction } from 'convex/react';
import { api } from '@/convex/_generated/api';
import { useQuery } from 'convex/react';
import { useToast } from '@/components/ui/use-toast';
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogFooter,
} from '@/components/ui/dialog';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Id } from '@/convex/_generated/dataModel';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { Loader, LOADER_MESSAGES } from '@/components/ui/loader';
import { useConfetti } from '@/hooks/useConfetti';
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';
import { GeneratingDialog } from '@/components/ui/generating-dialog';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import ReactMarkdown from 'react-markdown';
import { CopyIcon } from '@/components/ui/icons/copy-icon';
import { ActivityIcon } from '@/components/ui/icons/activity-icon';
import { Switch } from '@/components/ui/switch';
import { SlideSelector } from '@/components/ui/slide-selector';
import { SmallSlideSelector } from '@/components/ui/small-slide-selector';
import {
	Tooltip,
	TooltipTrigger,
	TooltipContent,
	TooltipProvider,
} from '@/components/ui/tooltip';
import { Loader2 } from 'lucide-react';
import { TEMPLATE_OPTIONS } from '@/convex/prompts/socialContent';
import { PublishSuccessDialog } from '@/components/shared/PublishSuccessDialog';
import { BrandVoicePill } from '@/components/admin/BrandVoicePill';
import { CircleChevronUpIcon } from '@/components/ui/icons/circle-chevron-up-icon';
import { CircleChevronDownIcon } from '@/components/ui/icons/circle-chevron-down-icon';
import { MiniPostAnalytics } from '@/components/shared/MiniPostAnalytics';
import { extractPublishResultSummary } from '@/utils/uploadPostResponses';
import {
	getBuzzTopicContext,
	getBuzzTopicMeta,
	getBuzzTopicPreviewItem,
	getBuzzTopicTitle,
} from '@/components/admin/content-machine/utils/buzzTopicPreview';
import { ContentMachineAdminLayout } from './ContentMachineAdminLayout';

interface ContentMachineProfile {
	_id: Id<'profiles'>;
	profileTitle: string;
	email: string;
	userId?: Id<'users'>;
	profileKey: string;
	writingAnalysis?: string;
	linkStatus?: {
		status: string;
		platform: string;
	};
	linkStatuses?: Array<{
		status: string;
		platform: string;
		lastUpdated: string;
		details?: string;
	}>;
}

interface Pod {
	_id: Id<'engagementPods'>;
	userId: Id<'users'>;
	platform: string;
	name: string;
	members: Array<{
		email: string;
		socialUsername: string;
		/** Compatibility alias: stores the Upload Post username/profileKey in v1. */
		uploadPostProfileKey: string;
	}>;
	isActive: boolean;
	createdAt: string;
	updatedAt: string;
	lastStatus?: {
		status: string;
		message: string;
		timestamp: string;
	};
	brandVoice?: {
		id: string;
		profileId?: Id<'profiles'>;
	};
	scheduledStart?: {
		timestamp: string;
		timezone: string;
		recurring?: string;
		recurringDays?: string[];
		lastRun?: string;
	};
	mode?: 'broadcast' | 'swarm';
	runCount?: number;
}

const canonicalSocialPlatform = (platform?: string | null) => {
	const normalized = String(platform || '')
		.toLowerCase()
		.replace(/[\s_-]+/g, '');
	if (['x', 'twitter', 'twitterx', 'xtwitter'].includes(normalized)) {
		return 'twitter';
	}
	return normalized;
};

const getPodPlatformLabel = (platform?: string | null) => {
	return canonicalSocialPlatform(platform) === 'twitter'
		? 'X'
		: String(platform || 'Unknown platform');
};

const getPodModeCapability = (
	platform: string | null | undefined,
	mode: 'broadcast' | 'swarm' | string | null | undefined,
) => {
	const normalizedMode = mode === 'broadcast' ? 'broadcast' : 'swarm';
	const isTwitter = canonicalSocialPlatform(platform) === 'twitter';
	const enabled = isTwitter && normalizedMode === 'swarm';
	return {
		enabled,
		reason: enabled
			? undefined
			: `${getPodPlatformLabel(platform)} ${normalizedMode} pods are disabled for this spec`,
	};
};

const isPodModeSupported = (
	platform: string | null | undefined,
	mode: 'broadcast' | 'swarm' | string | null | undefined,
) => getPodModeCapability(platform, mode).enabled;

interface BrandVoiceOption {
	id: string;
	name: string;
	description: string;
	emoji: string;
	style: string;
}

const BRAND_VOICES: BrandVoiceOption[] = [
	{
		id: 'prometheus',
		name: 'Prometheus',
		description: 'Social media influencer style',
		emoji: '🔥',
		style: 'Engaging and influential social media personality',
	},
	{
		id: 'reverend',
		name: 'The Reverend',
		description: 'Aggressive, story-driven, informal, and punchy',
		emoji: '🎭',
		style: 'Bold and dramatic storyteller',
	},
	{
		id: 'brent',
		name: 'Brent Ecomm Master',
		description: 'Professional, conversational, and accessible',
		emoji: '💼',
		style: 'Approachable business expert',
	},
];

const brandVoiceStyles = {
	base: 'relative flex items-start space-x-4 p-6 rounded-lg transition-all duration-200 bg-white dark:bg-gray-900 cursor-pointer',
	selected:
		'border-2 border-pink-500 shadow-[0_0_35px_rgba(236,72,153,0.6)] ring-2 ring-pink-500 scale-[1.02]',
	unselected:
		'border border-gray-50/10 hover:border-2 hover:border-purple-500 hover:shadow-[0_0_15px_rgba(147,51,234,0.3)] hover:scale-[1.02]',
	radio:
		'peer absolute left-4 top-[50%] translate-y-[-50%] h-5 w-5 border-2 border-pink-300 data-[state=checked]:border-pink-500 data-[state=checked]:bg-pink-500 data-[state=checked]:text-white',
	radioIcon:
		'absolute left-[1.5px] top-[1.5px] h-4 w-4 scale-0 text-current transition-transform duration-200 data-[state=checked]:scale-100',
	content: 'pl-8 flex-1',
};

function PublishedPill() {
	return (
		<span className='px-2 py-1 rounded-full text-xs font-semibold bg-gradient-to-r from-purple-500 via-purple-600 to-purple-700 text-white'>
			🚀 Published
		</span>
	);
}

interface GeneratedContent {
	success: boolean;
	content: string;
	error?: string;
	buzzUrl?: string;
}

export function ContentMachineAdmin() {
	// Navigation hooks
	const { id } = useParams<{ id: string }>();
	const navigate = useNavigate();

	// Basic hooks
	const { triggerConfetti, Confetti } = useConfetti();
	const { toast } = useToast();
	const publishPost = useAction(api.publishing.publishPost);
	const { user, isAdmin, isSuperAdmin } = useAuth();

	// Handle no ID case with useEffect instead of early return
	useEffect(() => {
		if (!id) {
			navigate('/admin/content-machine');
		}
	}, [id, navigate]);

	// State hooks
	const [currentTimestamp, setCurrentTimestamp] = useState<
		number | undefined
	>();
	const [engineToDelete, setEngineToDelete] =
		useState<Id<'contentMachine'> | null>(null);
	const [engineToRename, setEngineToRename] = useState<{
		id: Id<'contentMachine'>;
		name: string;
	} | null>(null);
	const [nextContent, setNextContent] = useState<string>('');
	const [buzzUrl, setBuzzUrl] = useState<string>('');
	const [previousTimestamps, setPreviousTimestamps] = useState<number[]>([]);
	const [historyPage, setHistoryPage] = useState<number>(0);
	const [isAnimating, setIsAnimating] = useState<boolean>(false);
	const [slideDirection, setSlideDirection] = useState<'up' | 'down'>('up');
	const [isRunning, setIsRunning] = useState(false);
	const [selectedProfile, setSelectedProfile] =
		useState<ContentMachineProfile | null>(null);
	const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [showSettings, setShowSettings] = useState<boolean>(false);
	const [showBuzz, setShowBuzz] = React.useState(false);
	const [isHovered, setIsHovered] = React.useState(false);
	const [showPublishDialog, setShowPublishDialog] = useState(false);
	const [isApproving, setIsApproving] = useState(false);
	const [approvedContent, setApprovedContent] = useState<string>('');
	const [approvedContentHistoryId, setApprovedContentHistoryId] = useState<
		Id<'contentHistory'> | undefined
	>();
	const [isPublished, setIsPublished] = useState(false);
	const [isPublishing, setIsPublishing] = useState(false);
	const [selectedTemplate, setSelectedTemplate] = useState<string>('');
	const [randomTemplate, setRandomTemplate] = useState<string>('');
	const [showDeleteContentConfirm, setShowDeleteContentConfirm] =
		useState(false);
	const [publishedPostUrl, setPublishedPostUrl] = useState<string>('');
	const [showPublishSuccess, setShowPublishSuccess] = useState(false);
	const [currentTemplate, setCurrentTemplate] = useState<string | undefined>();
	const [selectedPod, setSelectedPod] = useState<Pod | null>(null);
	const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);
	const [isUpButtonHovered, setIsUpButtonHovered] = useState<boolean>(false);
	const [isDownButtonHovered, setIsDownButtonHovered] =
		useState<boolean>(false);
	const [historyCursor, setHistoryCursor] = useState<
		Id<'contentHistory'> | undefined
	>(undefined);
	const [previousCursors, setPreviousCursors] = useState<
		Id<'contentHistory'>[]
	>([]);
	const [previousPage, setPreviousPage] = useState<number>(1);
	const [previousTotal, setPreviousTotal] = useState<number>(1);
	const [totalHistoryCount, setTotalHistoryCount] = useState<number>(0);
	const [publishedTemplateName, setPublishedTemplateName] = useState<
		string | undefined
	>();
	const [selectedBrandVoice, setSelectedBrandVoice] =
		useState<string>('prometheus'); // Default to prometheus
	const [userBrandVoice, setUserBrandVoice] = useState<any>(null);
	const [showAnalysisDialog, setShowAnalysisDialog] = useState(false);
	const [currentAnalysis, setCurrentAnalysis] = useState<string | null>(null);
	const [isUpdatingBrandVoice, setIsUpdatingBrandVoice] = useState(false);
	const [brandVoiceToConfirm, setBrandVoiceToConfirm] = useState<{
		value: string;
		name: string;
		description: string;
		emoji: string;
	} | null>(null);
	const [viewingBrandVoice, setViewingBrandVoice] = useState<{
		id: string;
		name: string;
		emoji: string;
	} | null>(null);
	const [selectedBuzzAgent, setSelectedBuzzAgent] = useState<string>('');
	const [selectedBuzzFeedItem, setSelectedBuzzFeedItem] =
		useState<string>('auto');
	const [selectedResearchItem, setSelectedResearchItem] =
		useState<string>('auto');

	// Query hooks
	const contentMachineQuery = useQuery(
		api.contentMachine.get,
		id
			? {
					id: id as unknown as Id<'contentMachine'>,
					requestingUserId: user?.id as Id<'users'> | undefined,
				}
			: 'skip',
	);
	const profiles = useQuery(
		api.profiles.getLinkedProfilesForPlatform,
		contentMachineQuery?.platform && user?.id
			? {
					platform: contentMachineQuery.platform,
					requestingUserId: user.id as any,
				}
			: 'skip',
	);

	// Filter profiles based on platform
	const filteredProfiles = profiles?.filter((profile: any) => {
		const machinePlatform = canonicalSocialPlatform(
			contentMachineQuery?.platform,
		);
		if (machinePlatform === 'twitter') {
			const linkedTwitterStatus = profile.linkStatuses?.find((status: any) => {
				const isLinked = status.isLinked ?? status.status === 'linked';
				return (
					isLinked && canonicalSocialPlatform(status.platform) === 'twitter'
				);
			});
			return !!linkedTwitterStatus;
		}

		// Original check for other platforms
		const linkStatus = profile.linkStatuses?.find(
			(status: any) =>
				canonicalSocialPlatform(status.platform) === machinePlatform,
		);
		return linkStatus?.isLinked ?? linkStatus?.status === 'linked';
	});

	const swarmPods = useQuery(
		api.engagementPods.list,
		user?.id
			? {
					userId: null,
					requestingUserId: user.id as unknown as any,
				}
			: 'skip',
	)?.filter(
		(pod: Pod) =>
			(pod.mode === 'swarm' || !pod.mode) &&
			isPodModeSupported(pod.platform, 'swarm') &&
			canonicalSocialPlatform(pod.platform) ===
				canonicalSocialPlatform(contentMachineQuery?.platform),
	);

	const connectedProfile = useQuery(
		api.profiles.getById,
		contentMachineQuery?.profileId
			? { profileId: contentMachineQuery.profileId }
			: 'skip',
	);

	const adminPreviewProfile = useQuery(api.profiles.getProfileByEmail, {
		email: 'matt@thetrainermethod.com',
	});

	const brentProfile = useQuery(api.profiles.getProfileByEmail, {
		email: 'brent@tortugaseo.com',
	});

	const reverendProfile = useQuery(api.profiles.getProfileByEmail, {
		email: 'mmiinnovations@gmail.com',
	});

	const documents = useQuery(api.documents.listDocuments, {});

	const feedItem = useQuery(
		api.rssFeedItems.getNextAvailableItem,
		contentMachineQuery?.feedId
			? { feedId: contentMachineQuery.feedId }
			: 'skip',
	);

	// Query to fetch buzz agents for the user
	const buzzAgents = useQuery(
		api.buzzAgents.list,
		user?.id
			? {
					userId: user.id as any,
					includePublic: true, // Include public agents to get the Prometheus agent
				}
			: 'skip',
	);

	// Query to fetch available Buzz topics for the selected agent
	const availableBuzzFeedItems = useQuery(
		api.buzzAgentFeedItems.getAvailableItemsForMachine,
		selectedBuzzAgent &&
			selectedBuzzAgent.startsWith('buzz_') &&
			contentMachineQuery?._id
			? {
					agentId: selectedBuzzAgent.replace(
						'buzz_',
						'',
					) as unknown as Id<'buzzAgents'>,
					machineId: contentMachineQuery._id,
					limit: 50,
				}
			: 'skip',
	);
	const buzzTopicPreviewItem = React.useMemo(
		() => getBuzzTopicPreviewItem(availableBuzzFeedItems, selectedBuzzFeedItem),
		[availableBuzzFeedItems, selectedBuzzFeedItem],
	);
	const buzzTopicPreviewTitle = getBuzzTopicTitle(buzzTopicPreviewItem);
	const buzzTopicPreviewContext = getBuzzTopicContext(buzzTopicPreviewItem);
	const buzzTopicPreviewMeta = getBuzzTopicMeta(buzzTopicPreviewItem);

	// Query to check if user has any research items at all
	const userHasResearchItems = useQuery(
		api.researchAgent.list,
		user?.id
			? {
					userId: user.id as any,
					numItems: 1, // Just check if any exist
				}
			: 'skip',
	);

	// Query to fetch available research items for this machine
	// Load independently of selection to avoid circular dependency
	const availableResearchItems = useQuery(
		api.researchAgent.getAvailableItemsForMachine,
		contentMachineQuery?._id && user?.id
			? {
					machineId: contentMachineQuery._id,
					userId: user.id as any,
				}
			: 'skip',
	);

	const contentHistoryQuery = useQuery(api.contentMachine.getContentHistory, {
		machineId: id as unknown as Id<'contentMachine'>,
		limit: 3,
		before: currentTimestamp,
	});

	const contentHistory = contentHistoryQuery?.items || [];
	const totalCount = contentHistoryQuery?.totalCount || 0;

	// Mutation hooks
	const updateName = useMutation(api.contentMachine.updateName);
	const deleteEngine = useMutation(api.contentMachine.deleteEngine);
	const runEngine = useAction(api.contentMachine.run);
	const approveContent = useMutation(api.contentMachine.approveContent);
	const updateProfile = useMutation(api.contentMachine.updateProfile);
	const toggleActive = useMutation(api.contentMachine.toggleActive);
	const updateBrandVoice = useMutation(api.contentMachine.updateBrandVoice);
	const deleteContent = useMutation(api.contentMachine.deleteContent);
	const updatePod = useMutation(api.contentMachine.updatePod);
	const updateBuzzAgent = useMutation(api.contentMachine.updateBuzzAgent);
	const updateShowBuzz = useMutation(api.contentMachine.updateShowBuzz);

	// Sync buzz toggle state from database
	React.useEffect(() => {
		if (contentMachineQuery?.showBuzz !== undefined) {
			setShowBuzz(contentMachineQuery.showBuzz);
		}
	}, [contentMachineQuery?.showBuzz]);

	// Sync selected buzz agent from database
	React.useEffect(() => {
		if (
			contentMachineQuery?.agentType === 'buzz' &&
			contentMachineQuery?.buzzAgentId
		) {
			setSelectedBuzzAgent(`buzz_${contentMachineQuery.buzzAgentId}`);
		} else if (
			contentMachineQuery?.agentType === 'research' &&
			contentMachineQuery?.agentId
		) {
			setSelectedBuzzAgent('research');
		} else if (!contentMachineQuery?.agentType && buzzAgents) {
			// If no agent type is set, look for the "🔥 Prometheus" agent as default
			const prometheusAgent = buzzAgents.find(
				(agent: any) => agent.name === '🔥 Prometheus',
			);
			if (prometheusAgent) {
				setSelectedBuzzAgent(`buzz_${prometheusAgent._id}`);
			}
		}
	}, [
		contentMachineQuery?.agentType,
		contentMachineQuery?.buzzAgentId,
		contentMachineQuery?.agentId,
		contentMachineQuery?.feedId,
		buzzAgents,
	]);

	// Helper functions
	const pickNewRandomTemplate = useCallback(() => {
		const randomIndex = Math.floor(Math.random() * TEMPLATE_OPTIONS.length);
		setRandomTemplate(TEMPLATE_OPTIONS[randomIndex].value);
	}, []);

	// Define handleRun before any useEffects
	const handleRun = useCallback(async () => {
		if (!contentMachineQuery?._id) return;

		setIsRunning(true);
		setError(null);

		try {
			if (
				showBuzz &&
				selectedBuzzAgent?.startsWith('buzz_') &&
				(contentMachineQuery.agentType !== 'buzz' ||
					`buzz_${contentMachineQuery.buzzAgentId}` !== selectedBuzzAgent)
			) {
				const actualId = selectedBuzzAgent.replace('buzz_', '');
				await updateBuzzAgent({
					machineId: contentMachineQuery._id,
					buzzAgentId: actualId
						? (actualId as unknown as Id<'buzzAgents'>)
						: null,
					agentType: 'buzz',
					agentId: actualId,
				});
			}

			const result = await runEngine({
				id: contentMachineQuery._id,
				writingAnalysis:
					selectedBrandVoice === 'user'
						? connectedProfile?.writingAnalysis || ''
						: selectedBrandVoice === 'brent'
							? brentProfile?.writingAnalysis || ''
							: reverendProfile?.writingAnalysis || '',
				includeBuzz: showBuzz,
				templateId: selectedTemplate || randomTemplate,
				buzzFeedItemId:
					selectedBuzzFeedItem && selectedBuzzFeedItem !== 'auto'
						? (selectedBuzzFeedItem as unknown as Id<'buzzAgentFeedItems'>)
						: undefined,
				researchAgentId:
					selectedResearchItem && selectedResearchItem !== 'auto'
						? (selectedResearchItem as unknown as Id<'researchAgent'>)
						: undefined,
			});

			if (result.error) {
				setError(result.error);
				toast({
					title: 'Error',
					description: result.error,
					variant: 'destructive',
				});
			} else if (result.content) {
				setNextContent(result.content || '');
				setBuzzUrl(result.buzzUrl || '');
				// Reset the selected Buzz topic after successful generation
				setSelectedBuzzFeedItem('auto');
			}
		} catch (err) {
			console.error('Error running engine:', err);
			setError(err instanceof Error ? err.message : 'Unknown error occurred');
			toast({
				title: 'Error',
				description:
					err instanceof Error ? err.message : 'Unknown error occurred',
				variant: 'destructive',
			});
		} finally {
			setIsRunning(false);
		}
	}, [
		contentMachineQuery?._id,
		contentMachineQuery?.agentType,
		contentMachineQuery?.buzzAgentId,
		selectedBrandVoice,
		connectedProfile?.writingAnalysis,
		brentProfile?.writingAnalysis,
		reverendProfile?.writingAnalysis,
		runEngine,
		showBuzz,
		selectedTemplate,
		randomTemplate,
		selectedBuzzFeedItem,
		selectedResearchItem,
		selectedBuzzAgent,
		updateBuzzAgent,
		toast,
	]);

	// Move all useEffect hooks together and ensure they're called unconditionally
	useEffect(() => {
		pickNewRandomTemplate();
	}, [pickNewRandomTemplate]);

	useEffect(() => {
		const handleGenerateContent = () => {
			if (!isRunning && contentMachineQuery?.profileId) {
				handleRun();
			}
		};

		document.addEventListener(
			'trigger-content-generation',
			handleGenerateContent,
		);
		return () => {
			document.removeEventListener(
				'trigger-content-generation',
				handleGenerateContent,
			);
		};
	}, [handleRun, isRunning, contentMachineQuery?.profileId]);

	useEffect(() => {
		if (connectedProfile) {
			setUserBrandVoice({
				id: 'user',
				name: `${connectedProfile.profileTitle || 'Loading...'} Brand Voice`,
				description: connectedProfile.writingAnalysis
					? 'Your personalized brand voice based on your content'
					: 'No writing analysis available yet',
				emoji: '✨',
				style: connectedProfile.writingAnalysis
					? 'Custom brand voice based on your content'
					: 'Add some content to generate your brand voice',
			});
			if (!contentMachineQuery?.brandVoice?.id) {
				setSelectedBrandVoice('user');
			}
		}
	}, [connectedProfile, contentMachineQuery?.brandVoice?.id]);

	useEffect(() => {
		if (contentMachineQuery?.brandVoice?.id) {
			setSelectedBrandVoice(contentMachineQuery.brandVoice.id);
		}
	}, [contentMachineQuery?.brandVoice?.id]);

	useEffect(() => {
		if (contentMachineQuery?.profileId) {
			setShowSettings(false);
		} else {
			setShowSettings(true);
		}
	}, [contentMachineQuery?.profileId]);

	// Update previous values when new data arrives
	useEffect(() => {
		if (contentHistoryQuery?.currentPage) {
			setPreviousPage(contentHistoryQuery.currentPage);
		}
		if (contentHistoryQuery?.totalPages) {
			setPreviousTotal(contentHistoryQuery.totalPages);
		}
	}, [contentHistoryQuery?.currentPage, contentHistoryQuery?.totalPages]);

	// Don't render anything if no ID - component will navigate away
	if (!id) {
		return null;
	}

	if (!contentMachineQuery) {
		const randomMessage =
			LOADER_MESSAGES[Math.floor(Math.random() * LOADER_MESSAGES.length)];
		return (
			<div className='flex items-center justify-center h-screen'>
				<Loader message={randomMessage} />
			</div>
		);
	}

	// Rest of the component logic...

	// Update handleApprove to store template info
	const handleApprove = async () => {
		try {
			if (!nextContent) return;
			setIsApproving(true);

			// Get the current template name
			let templateName: string | undefined;
			if (selectedTemplate === 'none') {
				templateName = 'No Template';
			} else if (selectedTemplate && selectedTemplate !== '__NO_TEMPLATE__') {
				templateName = TEMPLATE_OPTIONS.find(
					(opt) => opt.value === selectedTemplate,
				)?.label;
			} else {
				templateName = TEMPLATE_OPTIONS.find(
					(opt) => opt.value === randomTemplate,
				)?.label;
			}

			setCurrentTemplate(templateName);

			const approval = await approveContent({
				id: contentMachineQuery._id,
				content: nextContent,
				buzzUrl: buzzUrl || undefined,
				templateName,
			});

			setApprovedContent(nextContent);
			setApprovedContentHistoryId(
				approval?.contentHistoryId as Id<'contentHistory'> | undefined,
			);
			setNextContent('');
			if (selectedTemplate === '' || selectedTemplate === 'random') {
				pickNewRandomTemplate();
			}
			setShowPublishDialog(true);
		} catch (error) {
			console.error('Error approving content:', error);
			toast({
				title: 'Error',
				description:
					error instanceof Error ? error.message : 'Failed to approve content',
				variant: 'destructive',
				duration: 3000,
			});
			// Only clear state on error
			setNextContent('');
			setBuzzUrl('');
		} finally {
			setIsApproving(false);
		}
	};

	const handlePublishNow = async () => {
		try {
			setIsPublishing(true);
			setIsPublished(false);
			setShowPublishSuccess(false);
			setPublishedPostUrl('');
			if (!approvedContent.trim()) {
				toast({
					title: 'Error',
					description: 'No approved content available to publish.',
					variant: 'destructive',
				});
				return;
			}
			if (!contentMachineQuery?.profileId || !contentMachineQuery?.profileKey) {
				toast({
					title: 'Error',
					description:
						'No profile connected or profile key missing. Please connect a profile first.',
					variant: 'destructive',
				});
				return;
			}

			const fallbackContentHistoryId = contentHistory.find(
				(item: {
					_id: Id<'contentHistory'>;
					content?: string;
					status?: string;
				}) => item.content === approvedContent && item.status === 'approved',
			)?._id;
			const contentHistoryId =
				approvedContentHistoryId || fallbackContentHistoryId;
			const publishArgs: {
				postText: string;
				platforms: string[];
				profileKey: string;
				machineId: Id<'contentMachine'>;
				contentHistoryId?: Id<'contentHistory'>;
			} = {
				postText: approvedContent,
				platforms: [contentMachineQuery.platform],
				profileKey: contentMachineQuery.profileKey,
				machineId: contentMachineQuery._id,
			};
			if (contentHistoryId) publishArgs.contentHistoryId = contentHistoryId;

			const response = await publishPost(publishArgs);

			const publishSummary = extractPublishResultSummary(
				response,
				contentMachineQuery.platform,
			);

			if (publishSummary.postUrl) {
				triggerConfetti();
				setPublishedPostUrl(publishSummary.postUrl);
				setPublishedTemplateName(currentTemplate);
				setShowPublishSuccess(true);
				setIsPublished(true);
			} else if (publishSummary.jobId || publishSummary.requestId) {
				toast({
					title:
						publishSummary.status === 'scheduled'
							? 'Publish Scheduled'
							: 'Still Publishing',
					description:
						publishSummary.message ||
						'Upload Post is still processing this publish. Keep this content in history; the published URL will appear when processing finishes.',
					duration: 5000,
				});
			} else {
				toast({
					title: 'Publish Submitted',
					description:
						'The publish request completed, but no published URL was returned yet.',
					duration: 5000,
				});
			}

			setShowPublishDialog(false);
		} catch (error) {
			if (
				error instanceof Error &&
				error.message.replace(/[\s\S]*Error:\s*/, '').trim() ===
					'duplicate_content'
			) {
				toast({
					title: '⚠️ Duplicate Content Detected',
					description: (
						<div className='space-y-2'>
							<p className='text-sm'>
								This content (or something similar) was recently posted.
							</p>
							<div className='mt-2'>
								<p className='text-sm font-medium'>Options:</p>
								<ul className='text-sm list-none space-y-1 mt-1'>
									<li>⏳ Wait 48 hours before reposting</li>
									<li>✨ Generate new unique content</li>
									<li>✏️ Edit the content to be more unique</li>
								</ul>
							</div>
						</div>
					),
					variant: 'default',
				});
			} else {
				console.error('Error publishing post:', error);
				const errorMessage =
					error instanceof Error ? error.message : 'Failed to publish post';
				toast({
					title: 'Error',
					description: errorMessage,
					variant: 'destructive',
				});
			}
		} finally {
			setIsPublishing(false);
		}
	};

	const handlePublishSuccessClose = () => {
		setShowPublishSuccess(false);
		// Clean up after the success dialog is closed
		setApprovedContent('');
		setBuzzUrl('');
		setCurrentTemplate(undefined);
		setSelectedTemplate('');
		setPublishedTemplateName(undefined);
		setApprovedContentHistoryId(undefined);
	};

	const handleSchedule = () => {
		setShowPublishDialog(false);
		toast({
			title: 'Scheduling Not Enabled',
			description:
				'Upload Post scheduled publishing is not wired into this screen yet.',
			duration: 5000,
		});
	};

	const handleRename = (id: Id<'contentMachine'>, currentName: string) => {
		setEngineToRename({ id, name: currentName || '' });
	};

	const handleProfileSelect = async (profile: ContentMachineProfile | null) => {
		if (!contentMachineQuery) return;

		try {
			await updateProfile({
				id: contentMachineQuery._id,
				profileId: profile?._id || null,
			});
			setSelectedProfile(profile);
			if (profile) {
				triggerConfetti();
			}
			toast({
				title: profile ? '🔌 Profile Connected' : '🔌 Profile Disconnected',
				description: profile
					? `Successfully connected profile ${profile.profileTitle}`
					: 'Successfully disconnected profile',
				duration: 3000,
			});
		} catch (error) {
			toast({
				title: 'Error',
				description:
					error instanceof Error ? error.message : 'Failed to update profile',
				variant: 'destructive',
				duration: 3000,
			});
		}
	};

	const handlePodSelect = async (pod: Pod | null) => {
		if (!contentMachineQuery) return;

		try {
			if (pod) {
				const capability = getPodModeCapability(
					pod.platform,
					pod.mode || 'swarm',
				);
				if (!capability.enabled) {
					throw new Error(
						capability.reason ||
							`${getPodPlatformLabel(pod.platform)} ${pod.mode || 'swarm'} pods are disabled for this spec`,
					);
				}
			}
			await updatePod({
				machineId: contentMachineQuery._id,
				podId: pod?._id || null,
			});
			setSelectedPod(pod);
			if (pod?.mode === 'swarm') {
				triggerConfetti();
			}
			toast({
				title: pod ? '🔌 Pod Connected' : '🔌 Pod Disconnected',
				description: pod
					? `Successfully connected pod ${pod.name}`
					: 'Successfully disconnected pod',
				duration: 3000,
			});
		} catch (error) {
			toast({
				title: 'Error',
				description:
					error instanceof Error ? error.message : 'Failed to update pod',
				variant: 'destructive',
				duration: 3000,
			});
		}
	};

	const handleNavigation = (direction: 'up' | 'down') => {
		setIsAnimating(true);
		setSlideDirection(direction);

		setTimeout(() => {
			if (direction === 'up') {
				// Going up (showing newer items)
				if (previousTimestamps.length > 0) {
					const newTimestamp =
						previousTimestamps[previousTimestamps.length - 1];
					setCurrentTimestamp(newTimestamp);
					setPreviousTimestamps((prev) => prev.slice(0, -1));
				} else {
					setCurrentTimestamp(undefined);
				}
			} else {
				// Going down (showing older items)
				const nextCursor = contentHistoryQuery?.nextCursor;
				if (nextCursor) {
					setPreviousTimestamps((prev) =>
						currentTimestamp ? [...prev, currentTimestamp] : prev,
					);
					setCurrentTimestamp(nextCursor);
				}
			}
			setHistoryPage((prev) => (direction === 'up' ? prev - 1 : prev + 1));
		}, 200);

		setTimeout(() => {
			setIsAnimating(false);
		}, 400);
	};

	return (
		<ContentMachineAdminLayout
			id={id}
			navigate={navigate}
			Confetti={Confetti}
			contentMachineQuery={contentMachineQuery}
			profiles={profiles}
			filteredProfiles={filteredProfiles}
			swarmPods={swarmPods}
			selectedPod={selectedPod}
			handlePodSelect={handlePodSelect}
			connectedProfile={connectedProfile}
			showSettings={showSettings}
			setShowSettings={setShowSettings}
			handleRename={handleRename}
			toggleActive={toggleActive}
			toast={toast}
			selectedProfile={selectedProfile}
			handleProfileSelect={handleProfileSelect}
			setShowDisconnectConfirm={setShowDisconnectConfirm}
			nextContent={nextContent}
			setNextContent={setNextContent}
			selectedTemplate={selectedTemplate}
			setSelectedTemplate={setSelectedTemplate}
			randomTemplate={randomTemplate}
			setRandomTemplate={setRandomTemplate}
			buzzUrl={buzzUrl}
			setBuzzUrl={setBuzzUrl}
			showBuzz={showBuzz}
			setShowBuzz={setShowBuzz}
			updateShowBuzz={updateShowBuzz}
			selectedBuzzAgent={selectedBuzzAgent}
			setSelectedBuzzAgent={setSelectedBuzzAgent}
			selectedBuzzFeedItem={selectedBuzzFeedItem}
			setSelectedBuzzFeedItem={setSelectedBuzzFeedItem}
			selectedResearchItem={selectedResearchItem}
			setSelectedResearchItem={setSelectedResearchItem}
			handleRun={handleRun}
			isRunning={isRunning}
			setIsRunning={setIsRunning}
			pickNewRandomTemplate={pickNewRandomTemplate}
			userHasResearchItems={userHasResearchItems}
			availableResearchItems={availableResearchItems}
			availableBuzzFeedItems={availableBuzzFeedItems}
			buzzTopicPreviewItem={buzzTopicPreviewItem}
			buzzTopicPreviewTitle={buzzTopicPreviewTitle}
			buzzTopicPreviewContext={buzzTopicPreviewContext}
			buzzTopicPreviewMeta={buzzTopicPreviewMeta}
			buzzAgents={buzzAgents}
			updateBuzzAgent={updateBuzzAgent}
			contentHistoryQuery={contentHistoryQuery}
			contentHistory={contentHistory}
			historyPage={historyPage}
			previousPage={previousPage}
			previousTotal={previousTotal}
			handleNavigation={handleNavigation}
			isUpButtonHovered={isUpButtonHovered}
			setIsUpButtonHovered={setIsUpButtonHovered}
			isDownButtonHovered={isDownButtonHovered}
			setIsDownButtonHovered={setIsDownButtonHovered}
			isAnimating={isAnimating}
			slideDirection={slideDirection}
			engineToDelete={engineToDelete}
			setEngineToDelete={setEngineToDelete}
			deleteEngine={deleteEngine}
			engineToRename={engineToRename}
			setEngineToRename={setEngineToRename}
			updateName={updateName}
			showDisconnectConfirm={showDisconnectConfirm}
			error={error}
			setError={setError}
			userBrandVoice={userBrandVoice}
			selectedBrandVoice={selectedBrandVoice}
			setBrandVoiceToConfirm={setBrandVoiceToConfirm}
			isAdmin={isAdmin}
			documents={documents}
			setCurrentAnalysis={setCurrentAnalysis}
			setViewingBrandVoice={setViewingBrandVoice}
			setShowAnalysisDialog={setShowAnalysisDialog}
			BRAND_VOICES={BRAND_VOICES}
			brandVoiceStyles={brandVoiceStyles}
			brentProfile={brentProfile}
			reverendProfile={reverendProfile}
			showAnalysisDialog={showAnalysisDialog}
			viewingBrandVoice={viewingBrandVoice}
			currentAnalysis={currentAnalysis}
			brandVoiceToConfirm={brandVoiceToConfirm}
			setIsUpdatingBrandVoice={setIsUpdatingBrandVoice}
			adminPreviewProfile={adminPreviewProfile}
			updateBrandVoice={updateBrandVoice}
			isUpdatingBrandVoice={isUpdatingBrandVoice}
			showDeleteContentConfirm={showDeleteContentConfirm}
			setShowDeleteContentConfirm={setShowDeleteContentConfirm}
			deleteContent={deleteContent}
			showPublishDialog={showPublishDialog}
			setShowPublishDialog={setShowPublishDialog}
			isPublishing={isPublishing}
			handlePublishNow={handlePublishNow}
			handleSchedule={handleSchedule}
			showPublishSuccess={showPublishSuccess}
			handlePublishSuccessClose={handlePublishSuccessClose}
			publishedPostUrl={publishedPostUrl}
			publishedTemplateName={publishedTemplateName}
			handleApprove={handleApprove}
			isApproving={isApproving}
			approveContent={approveContent}
			approvedContent={approvedContent}
			currentTemplate={currentTemplate}
			setSelectedBrandVoice={setSelectedBrandVoice}
		/>
	);
}
